import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import API from "../../api/API";
import { AppConfirm } from "../../../utils/PopupUtil";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

interface MutationProps {
  tenantID: string;
  tenantSchemasQuantity: number;
}

const useCleanTenantSchemas = () => {
  const queryClient = useQueryClient();
  const tenantIDAtomValue = useAtomValue(tenantIDAtom);

  const confirmClearTenantSchemas = async (quantity: number) => {
    const result = await AppConfirm({
      title: "Confirm Tenant Schemas Deletion",
      text: `Are you sure you want to delete all tenant schemas except for the last ${quantity}?`,
    });

    return result.isConfirmed;
  };

  const cleanTenantSchemas = useMutation({
    mutationFn: async ({ tenantID, tenantSchemasQuantity }: MutationProps) => {
      newToastEvent("success", "Removing schemas...", `schemas-${tenantID}`, {
        loading: true,
        autoClose: false,
      });

      return API.cleanTenantSchemas(tenantID, tenantSchemasQuantity);
    },
    onSuccess: (data, { tenantID }) => {
      queryClient.invalidateQueries({
        queryKey: [tenantID || tenantIDAtomValue, "records", "TenantSchema"],
      });

      updateToastEvent("success", data?.data.message, `schemas-${tenantID}`, {
        loading: false,
        autoClose: true,
      });
    },
    onError: (err, { tenantID }) => {
      updateToastEvent(
        "error",
        `Error: ${getErrorMessage(err)}`,
        `schemas-${tenantID}`,
        {
          loading: false,
          autoClose: false,
        }
      );
    },
  });

  return {
    confirmClearTenantSchemas: confirmClearTenantSchemas,
    cleanTenantSchemas: cleanTenantSchemas.mutate,
    cleanTenantSchemasLoading: cleanTenantSchemas.isPending,
    cleanTenantSchemasError: cleanTenantSchemas.isError,
  };
};

export default useCleanTenantSchemas;
